/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Stockton Jenkins <stockton.jenkins@deliciousai.com>
 */

import {
  AddCircle,
  AutoAwesome,
  RemoveCircle,
  SwipeLeft,
} from '@mui/icons-material'
import { Paper, Stack, Typography } from '@mui/material'
import React from 'react'
import Markdown from 'react-markdown'

import { RecommendedSwapMetadataFragment } from '@/graphql'
import useT from '@/i18n/useT'
import { ProductImage } from '@/pages/dashboards/Default/ProductImage'
import { MarkdownHelpers } from '@/utils/markdown.helpers'

type RecommendedSwapInsightProps = {
  metadata: RecommendedSwapMetadataFragment
}

const RecommendedSwapInsight: React.FC<RecommendedSwapInsightProps> = ({
  metadata,
}) => {
  const { t } = useT()
  return (
    <Stack spacing={4}>
      <Stack direction={'row'} justifyContent={'space-between'} spacing={2}>
        <Stack direction={'row'} alignItems={'center'} spacing={2}>
          <AutoAwesome />
          <Typography variant="h5">
            {t('aiInsightsTable.recommendedSwap.identified')}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        pl={8}
        direction={'row'}
        alignItems={'center'}
        spacing={4}
        height={200}
      >
        <Stack spacing={2} sx={{ height: '100%' }}>
          <Paper
            variant={'outlined'}
            sx={{
              bgcolor: 'white',
              flex: 1,
              alignContent: 'center',
              position: 'relative',
            }}
          >
            <RemoveCircle
              color="error"
              sx={{ position: 'absolute', top: 8, right: 8 }}
            />
            <ProductImage
              upc={metadata.oldUpc}
              width="100px"
              height="auto"
              maxHeight={'150px'}
              minHeight={'100px'}
              objectFit={'contain'}
              skeleton
            />
          </Paper>
        </Stack>
        <Stack alignItems={'center'}>
          <SwipeLeft fontSize={'large'} />
          <Typography variant={'caption'} color="text.secondary" pt={1}>
            {t('aiInsightsTable.recommendedSwap.replace')}
          </Typography>
        </Stack>
        <Stack spacing={2} sx={{ height: '100%' }}>
          <Paper
            variant={'outlined'}
            sx={{
              bgcolor: 'white',
              flex: 1,
              alignContent: 'center',
              position: 'relative',
            }}
          >
            <AddCircle
              color="success"
              sx={{ position: 'absolute', top: 8, right: 8 }}
            />
            <ProductImage
              upc={metadata.newUpc}
              width="100px"
              height="auto"
              maxHeight={'150px'}
              minHeight={'100px'}
              objectFit={'contain'}
              skeleton
            />
          </Paper>
        </Stack>
      </Stack>
      <Markdown>{MarkdownHelpers.format(metadata.message)}</Markdown>
    </Stack>
  )
}
export default RecommendedSwapInsight
